import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Spin,
  Typography,
  DatePicker,
  Select,
  Space,
} from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { EmployeeFundService } from '../../../service/employee-service/Employee-fund.service';
import { EmployeeFundRecord } from '../../../model/EmployeeFundRecord.model';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface FundHistoryProps {
  employeeId: number;
}

const FundHistory: React.FC<FundHistoryProps> = ({ employeeId }) => {
  const [records, setRecords] = useState<EmployeeFundRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<EmployeeFundRecord[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  );
  const [selectedChangeType, setSelectedChangeType] = useState<string>('all');

  const fetchFundRecords = async () => {
    try {
      setLoading(true);
      const data = await EmployeeFundService.getEmployeeFundRecords(employeeId);
      setRecords(data);
      setFilteredRecords(data);
    } catch (error) {
      console.error('Error fetching fund records:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFundRecords();
  }, [employeeId]);

  // Lọc dữ liệu khi có thay đổi filter
  useEffect(() => {
    let filtered = [...records];

    // Lọc theo khoảng thời gian
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf('day');
      const endDate = dateRange[1].endOf('day');

      filtered = filtered.filter((record) => {
        const recordDate = dayjs(record.dateTime);
        return recordDate.isAfter(startDate) && recordDate.isBefore(endDate);
      });
    }

    // Lọc theo loại biến động
    if (selectedChangeType !== 'all') {
      filtered = filtered.filter(
        (record) => record.changeType === selectedChangeType
      );
    }

    setFilteredRecords(filtered);
  }, [records, dateRange, selectedChangeType]);

  const getChangeTypeLabel = (changeType: string) => {
    const typeMap: { [key: string]: { label: string; color: string } } = {
      company_transfer_in: { label: 'Nhận từ công ty', color: 'green' },
      company_transfer_out: { label: 'Chuyển về công ty', color: 'blue' },
      company_transfer_adjustment: {
        label: 'Sửa chuyển về công ty',
        color: 'orange',
      },
      company_transfer_cancel: { label: 'Hủy chuyển về công ty', color: 'green' },
      transaction_payment: { label: 'Thanh toán giao dịch', color: 'orange' },
      transaction_withdrawal: { label: 'Rút tiền giao dịch', color: 'red' },
      fund_reset: { label: 'Reset quỹ', color: 'purple' },
      maturity_transaction: { label: 'Giao dịch đáo hạn', color: 'cyan' },
      revert_transaction: { label: 'Hoàn tác giao dịch', color: 'gray' },
      maturity_payment: { label: 'Thanh toán kỳ đáo hạn', color: 'blue' },
      withdrawal_service: { label: 'Giao dịch rút tiền', color: 'orange' },
      withdrawal_pos_receive: { label: 'Nhận tiền từ POS', color: 'green' },
      withdrawal_pos_revert: {
        label: 'Hoàn tác nhận tiền từ POS',
        color: 'red',
      },
      cash_back_for_customer: {
        label: 'Hoàn tiền cho khách hàng',
        color: 'red',
      },
      cash_back_from_customer: {
        label: 'Hoàn tiền từ khách hàng',
        color: 'green',
      },
      adjustment: { label: 'Điều chỉnh', color: 'orange' },
      reset: { label: 'Reset số dư', color: 'volcano' },
    };

    return typeMap[changeType] || { label: changeType, color: 'default' };
  };

  // Lấy danh sách các loại biến động có trong dữ liệu
  const getAvailableChangeTypes = () => {
    const types = Array.from(
      new Set(records.map((record) => record.changeType))
    );
    return types.map((type) => ({
      value: type,
      label: getChangeTypeLabel(type).label,
    }));
  };

  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    setDateRange(dates);
  };

  const handleChangeTypeChange = (value: string) => {
    setSelectedChangeType(value);
  };

  const clearFilters = () => {
    setDateRange(null);
    setSelectedChangeType('all');
  };

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'dateTime',
      key: 'dateTime',
      render: (dateTime: string) => {
        return new Date(dateTime).toLocaleString('vi-VN');
      },
      sorter: (a: EmployeeFundRecord, b: EmployeeFundRecord) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Loại biến động',
      dataIndex: 'changeType',
      key: 'changeType',
      render: (changeType: string) => {
        const { label, color } = getChangeTypeLabel(changeType);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => {
        const isPositive = amount >= 0;
        return (
          <span
            className={`flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}
          >
            {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            {Math.abs(amount).toLocaleString('vi-VN')} VND
          </span>
        );
      },
      sorter: (a: EmployeeFundRecord, b: EmployeeFundRecord) =>
        a.amount - b.amount,
    },
    {
      title: 'Số dư trước',
      dataIndex: 'balanceBefore',
      key: 'balanceBefore',
      render: (balance: number) => balance.toLocaleString('vi-VN') + ' VND',
    },
    {
      title: 'Số dư sau',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (balance: number) => balance.toLocaleString('vi-VN') + ' VND',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => (
        <span className="text-sm text-gray-600" title={notes}>
          {notes && notes.length > 50 ? notes.substring(0, 50) + '...' : notes}
        </span>
      ),
    },
  ];

  return (
    <Card
      title={
        <div className="flex items-center gap-2">
          <Title level={4} style={{ margin: 0 }}>
            Lịch sử biến động quỹ
          </Title>
        </div>
      }
      bordered
      className="shadow-sm hover:shadow-md transition-shadow duration-300"
      extra={
        <Space>
          <RangePicker
            placeholder={['Từ ngày', 'Đến ngày']}
            value={dateRange}
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
          />
          <Select
            placeholder="Chọn loại biến động"
            value={selectedChangeType}
            onChange={handleChangeTypeChange}
            style={{ width: 200 }}
            allowClear
            options={[
              { value: 'all', label: 'Tất cả' },
              ...getAvailableChangeTypes(),
            ]}
          />
          {(dateRange || selectedChangeType !== 'all') && (
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Xóa bộ lọc
            </button>
          )}
        </Space>
      }
    >
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={filteredRecords}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} bản ghi`,
          }}
          scroll={{ x: 800 }}
        />
      )}
    </Card>
  );
};

export default FundHistory;
